import '../../../_metronic/_assets/plugins/flaticon/flaticon.css';
import '../../../_metronic/_assets/plugins/flaticon2/flaticon.css';
import { contentsApi } from '../../../api/contents';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useContext, useEffect, useState } from 'react';
import Card from '@mui/material/Card';
import { Row } from 'react-bootstrap';

import { useFbPixel } from '../../../hooks/FbPixel';
import { useQuery } from 'react-query';
import { checkoutsApi } from '../../../api/checkouts';
import { WhatsAppWidget } from '../../../components/widgets/WhatsAppWidget';
import { ExitIntentPopup } from '../components/ExitIntent';
import { IPayment } from '../../../api/payments/types/payment.type';
import axios from 'axios';
import { apiFactory } from '../../../api/ApiFactory';
import { ContentContext } from '../components/Context/ContentContext';
import { Sidebar } from '../components/Sidebar';
import {
	PageAsideBlocks,
	PageBlocks,
	PageFooterBlocks,
	useCheckoutPage
} from '../components/PageBlocks';
import { hasConfirmationPage } from '../components/PageBlocks/confirmation';
import {
	CheckoutPageContext,
	CheckoutPageConfirmationContext,
	CheckoutPageSlotsContext
} from '../components/Context/CheckoutPageContext';
import { isPreviewMode } from '../components/PageBlocks/use-preview-bridge';
import { useCheckoutTracking } from '../components/PageBlocks/tracking';
import {
	initBehaviorTracker,
	trackBehavior
} from '../components/PageBlocks/behavior-tracker';
import { ICoupon } from '../../../api/coupons/interfaces/couponInterface';
import { PaymentContext } from '../components/Context/PaymentContext';
import { Counter } from '../components/Counter';
import { CouponContext } from '../components/Context/CouponContext';
import { Form } from '../components/Form';
import { IContent } from '../../../api/contents/interfaces/contentInterface';
import {
	OrderBumpContext,
	OrderBumpSelection
} from '../components/Context/OrderBumpContext';
import { useGTM } from '../../../hooks/GTM';
import { useGoogleAnalytics } from '../../../hooks/Analytics';
import { BillingType } from '../components/constants/billingType';
import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { useBlockedPlatform } from '../../../hooks/use-blocked-platform';
import { BlockedLink } from '../BlockedLink';
import { PaymentsUnavailable } from '../PaymentsUnavailable';
import { arePaymentsUnavailable } from '../../../utils/payments-availability';
import { useCheckoutLinkGate } from '../components/hooks/useCheckoutLinkGate';
import { useCheckoutThemeReady } from '../components/hooks/usePageReady';
import { trackCheckoutOpen } from '../../../shared/waid-analytics';
import {
	useFinanceReady,
	waitsForFinance
} from '../../../hooks/use-finance-ready';

interface IParams extends Record<string, string> {
	slug: string;
	platform: string;
}

const { getBySlug } = contentsApi;
const { findByContent } = checkoutsApi;

export const SingleCheckout = () => {
	const navigate = useNavigate();

	const [billingType, setBillingType] = useState(BillingType.CreditCard);

	const [orderBumps, setOrderBumps] = useState<OrderBumpSelection[]>([]);
	const toggleOrderBump = (
		selection: OrderBumpSelection,
		checked: boolean
	) => {
		trackBehavior(
			'bump_toggled',
			undefined,
			`${checked ? 'on' : 'off'}:${selection.uuid.slice(0, 24)}`
		);
		setOrderBumps((prev) =>
			checked
				? [...prev.filter((b) => b.uuid !== selection.uuid), selection]
				: prev.filter((b) => b.uuid !== selection.uuid)
		);
	};

	const { slug, platform } = useParams<IParams>() as IParams;
	const { trackInitiateCheckout } = useFbPixel();
	const { pageView } = useGoogleAnalytics();
	useGTM();
	useBlockedPlatform();

	const {
		data: content,
		isLoading,
		isError: isContentError
	} = useQuery([getBySlug.key, { slug }], getBySlug.fetch, {
		refetchOnWindowFocus: false
	});

	// Produto sem checkout customizado responde 404 — é ausência de configuração,
	// não erro. Sem `retry: false` o react-query repetia a MESMA 404 quatro vezes
	// por carga; sem `enabled` ela ainda disparava antes de o conteúdo resolver.
	const { data: checkout, isLoading: isCheckoutLoading } = useQuery(
		[findByContent.key, { id: content?.id }],
		findByContent.fetch,
		{
			enabled: !!content?.id,
			retry: false,
			refetchOnWindowFocus: false
		}
	);

	const [searchParams] = useSearchParams();
	const hash = searchParams.get('hash') || undefined;
	// Skeleton até o tema/aparência da página do builder chegar: sem isso o
	// checkout aparece no layout padrão e troca pelo do admin na frente do
	// comprador.
	const isThemeReady = useCheckoutThemeReady(
		checkout?.id,
		!isContentError && (isLoading || !content || isCheckoutLoading),
		content?.uuid
	);
	const linkGate = useCheckoutLinkGate({
		checkout,
		hash,
		// The checkout query only starts once the content is loaded, so the
		// content chain counts as "still resolving" too. A failed content
		// request is NOT resolving — it would never settle and would pin the
		// skeleton forever; fall back to the previous behaviour instead.
		isCheckoutResolving:
			!isContentError && (isLoading || !content || isCheckoutLoading)
	});

	// Página customizada do checkout (builder do hub) — vazia = layout padrão.
	const page = useCheckoutPage(checkout?.id, content?.uuid);
	// Pixels da etapa de checkout (inertes no preview do builder).
	useCheckoutTracking(page?.settings?.tracking, 'checkout');

	// Inteligência de conversão: sessão de comportamento deste checkout.
	useEffect(() => {
		if (!checkout?.id) return;
		const prefillKey = 'checkout-prefill:' + window.location.pathname;
		let knownEmail: string | null = null;
		try {
			knownEmail =
				new URLSearchParams(window.location.search).get('email') ||
				JSON.parse(sessionStorage.getItem(prefillKey) || '{}').email ||
				null;
		} catch {
			knownEmail = null;
		}
		apiFactory
			.getInstance()
			.getApiKey()
			.then((apiKey) =>
				initBehaviorTracker({
					checkoutId: checkout.id,
					payUrl: process.env.REACT_APP_CURSEDUCA_PAY_DOMAIN || '',
					apiKey,
					knownEmail
				})
			)
			.catch(() => undefined);
	}, [checkout?.id]);

	const { finance } = useContext(ApplicationContext);
	// O formulário só aparece com o params (ou passados 4 s): sem isso o
	// comprador começava a preencher e a página virava "pagamentos
	// indisponíveis" na frente dele. Grátis não trava, então não espera.
	const isFinanceReady = useFinanceReady(
		finance,
		waitsForFinance(content?.pricing.price, isPreviewMode())
	);

	const [currentPrice, setCurrentPrice] = useState<number>(0);
	const [quantity, setQuantity] = useState<number>(1);
	const [coupon, setCoupon] = useState<ICoupon | null>();
	const [payment, setPayment] = useState<IPayment | null>(null);

	useEffect(() => {
		const href = window.location.pathname;

		const forbiddenPaths = [
			'/forumiep/p/forum-iep-estudantes-2-lote',
			'/forumiep/p/forum-iep-profissionais-2-lote'
		];

		if (forbiddenPaths.includes(href)) {
			if (href.startsWith('/forumiep')) {
				location.href = 'https://forumiep.curseduca.pro/l/';
			}
		}
	}, []);

	useEffect(() => {
		// Pré-visualização do builder: o admin está desenhando a página, não
		// comprando — não faz sentido mandá-lo para "checkout indisponível"
		// só porque a plataforma ainda não terminou de configurar o Waid Pay.
		// Para o comprador o bloqueio segue valendo.
		if (isPreviewMode()) return;

		if (!finance) return;
		if (finance.isCurseducaPayConfigured === true) return;

		if (!content) return;
		if (content.pricing.price === 0) return;

		navigate('/unavailable');
	}, [finance, content]);

	useEffect(() => {
		pageView();
	}, [pageView]);

	useEffect(() => {
		if (!content?.pricing.price) return;

		trackInitiateCheckout({
			value: content?.pricing.price,
			currency: 'BRL'
		});
	}, [trackInitiateCheckout, content]);

	// Waid Analytics: the checkout was landed on. Once the product resolved,
	// which is also when the platform api_key the client needs is in place.
	// The builder preview is the admin designing the page, not a visit.
	useEffect(() => {
		if (isPreviewMode() || !content?.id) return;
		void trackCheckoutOpen({
			type: 'single',
			product: { id: String(content.id), name: content.title }
		});
	}, [content?.id]);

	useEffect(() => {
		setCurrentPrice(content?.pricing.price || 0);
	}, [content]);

	const [platformApiKey, setPlatformApiKey] = useState('');

	useEffect(() => {
		apiFactory.isDefaultApiKeyDefined().then((response) => {
			if (response) return;
			axios
				.get<{ key: string }>(
					`${process.env.REACT_APP_CURSEDUCA_APPLICATION_DOMAIN}/platforms/slug/${platform}`
				)
				.then((response) => {
					apiFactory.enableCustom(response.data.key);
					setPlatformApiKey(response.data.key);
				});
		});

		return () => apiFactory.enableDefault();
	}, []);

	useEffect(() => {
		if (!window?.location) {
			return;
		}

		const path = window.location.pathname;
		const toRedirect = [
			'/forumiep/p/frum-iep-2024-estudantes-2o-lote-1713534226249',
			'/forumiep/p/frum-iep-2024-estudantes-1o-lote-1713534216722',
			'/forumiep/p/frum-iep-2024-profissionais-2o-lote-1713534204425',
			'/forumiep/p/frum-iep-2024-profissionais-1o-lote-1713534188714'
		];

		if (!toRedirect.includes(path)) {
			return;
		}

		location.href = 'https://forumiep.curseduca.pro/l/';
	}, []);

	// Mounted while the gate is still resolving as well: an invalid single-use
	// link must never render the form before redirecting.
	if (
		linkGate.isResolving ||
		linkGate.isBlocked ||
		!isThemeReady ||
		!isFinanceReady
	) {
		return (
			<BlockedLink
				isResolving={linkGate.isResolving || !linkGate.isBlocked}
			/>
		);
	}

	// Vendedor em análise ou pausado: o estado no lugar do formulário, para o
	// comprador não preencher tudo e descobrir no fim (pay #619).
	if (
		arePaymentsUnavailable(finance, {
			isFree: content?.pricing.price === 0,
			isPreview: isPreviewMode()
		})
	) {
		return <PaymentsUnavailable checkoutId={checkout?.id} />;
	}

	return (
		<CheckoutPageContext.Provider value={page?.settings ?? {}}>
			<CheckoutPageSlotsContext.Provider value={page?.slots ?? {}}>
				<CheckoutPageConfirmationContext.Provider
					value={hasConfirmationPage(page)}
				>
					<ContentContext.Provider
						value={{
							content,
							currentPrice,
							setCurrentPrice,
							quantity,
							setQuantity,
							isLoading,
							checkout,
							billingType,
							setBillingType
						}}
					>
						<PaymentContext.Provider
							value={{ payment, setPayment }}
						>
							<OrderBumpContext.Provider
								value={{
									orderBumps,
									toggleOrderBump,
									enabled: true
								}}
							>
								<CouponContext.Provider
									value={{
										coupon,
										setCoupon: (coupon) => setCoupon(coupon)
									}}
								>
									<div
										style={{
											backgroundImage: `url(${checkout?.backgroundImage})`,
											backgroundSize: 'cover'
										}}
										className="d-flex flex-column align-items-center"
									>
										<Counter />
										<div className="content w-100">
											<div className="container">
												{checkout?.headerImage && (
													<Row>
														<img
															className="w-100 h-100"
															src={
																checkout.headerImage
															}
														/>
													</Row>
												)}
												<PageBlocks
													page={page}
													checkoutId={checkout?.id}
												/>
												<div className="checkout-grid">
													<Card className="rounded-xl p-md-10 form-grid-item">
														<Form
															apiKey={
																platformApiKey
															}
														/>
													</Card>
													<div className="sidebar-grid-item">
														<Sidebar />
														<PageAsideBlocks
															page={page}
															checkoutId={
																checkout?.id
															}
														/>
													</div>
													<PageFooterBlocks
														page={page}
														checkoutId={
															checkout?.id
														}
													/>
												</div>
											</div>
										</div>
									</div>
									<WhatsAppWidget />
									<ExitIntentPopup
										settings={page?.settings?.exitIntent}
									/>
								</CouponContext.Provider>
							</OrderBumpContext.Provider>
						</PaymentContext.Provider>
					</ContentContext.Provider>
				</CheckoutPageConfirmationContext.Provider>
			</CheckoutPageSlotsContext.Provider>
		</CheckoutPageContext.Provider>
	);
};
